// 花嫁「返しの一閃」(veilSweepLink) を、祈り → 払い → ハートを1つ飛ばす技にした比較版 (2026-10-23・開発版だけ)。
//
// 作者の答え (返しの一閃の答えるページ・2026-10-22 夜・台帳 docs/ledger/bosses.md)：
//   問1 祈り＝「立って胸の前で指を組む」／問2 飛ばす物＝「ハートと花びらの帯」／
//   問3 弾き方＝まとめて1回・メモ「１つのハートを飛ばす技で、その軌道に花びらが舞うようにして」。
// 作り：
//   体＝祈り (kaeshi_pray・指を組む) で溜め → 払い (kaeshi_sweep・片手を相手へ払い出す) の瞬間にハートが手から出る。
//   当たり＝ハートそのもの (飛ぶ弾・castProjectile)。花びらは見た目だけで当たらない。弾くのは1回。
//   弾いた時の扱いは他の飛ぶ弾と同じ (ちょうどで弾けば花嫁の胸へ跳ね返る・それ以外は消える・hits.ts)。
//   ハートと花びらの絵は Codex に描かせた部品 (public/sprites/fx/bride/kaeshi_*.png)。読めない間は同じ形をコードで描く。
//   赤・朱・マゼンタは使わない (赤＝弾けない攻撃の専用色)。白い線・光の筋は描かない。
// 配布版は URL を読まない (setBrideKaeshi が import.meta.env.DEV の時だけ入れる)。
//   ⚠️ 識別子に URL のキーの綴りを入れない (scripts/check_dev_leak.mjs)。
// 記録 = docs/research/2026-10-23-bride-issen-kaeshi.md

import { ATTACKS } from './attacks'
import { BOSS_ATTACK_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import type { ActiveHit, FxEvent } from './types'
import { setBrideKaeshiBlade } from './bride_kaeshi_blade'
import { setBrideKaeshiTween } from './bride_kaeshi_tween'

// ───────── 技の数値 ─────────
/** ハートの速さ (px/秒)。介錯の直射弾 620 より少し遅く、飛んでくるのを目で追える速さ。 */
export const KAESHI_HEART_SPEED = 520
/** ハートの当たりの幅 = 描くハートの幅。 */
export const KAESHI_HEART_W = 44
/** 払いの手の位置 (花嫁の中心から前へ・床から上)。kaeshi_sweep の開いた手のひら (絵の画素 前へ 798・上へ 710 × 168/1262) に合わせた。 */
export const KAESHI_HAND_FORWARD = 106
export const KAESHI_HAND_UP = 94
/** 手の高さから主人公の胸へ下りていく角度 (度)。300px 先で約 60px 下がる。 */
export const KAESHI_HEART_DESCENT_DEG = 11
/** 花嫁が寄って止まる間合い。払い出す腕の前の空間を空けて、ハートが飛ぶのを見せる。 */
export const KAESHI_APPROACH_PX = 220
/** ゆっくり版 (?brideKaeshi=slow・10-23 窓口「届くまで約 0.3 秒に」)。new は止まる間合い 220px で届くまで約 0.13 秒。
 *  遅くするだけだとハートが浮いて見えるので、止まる間合いを 60px 広げて飛ぶ距離を伸ばし、速さは少しだけ落とした。
 *  弾きの受付はハートが主人公に触れた瞬間に開く (飛ぶ弾は重なりで判定・hits.ts) ので、速さと間合いを変えても届く瞬間とずれない
 *  (tests/bride_kaeshi_reach.test.ts が届くまでの時間と弾ける押しどころを測る)。 */
export const KAESHI_SLOW_HEART_SPEED = 430
export const KAESHI_SLOW_APPROACH_PX = 280

export const BRIDE_KAESHI_TIMELINE: BossAttackTimeline = [
  { phase: 'telegraph', at: 0.0, image: 'kaeshi_pray', hold: true, tag: 'anticipation' },
  { phase: 'active', at: 0.0, image: 'kaeshi_sweep', hold: true, tag: 'contact' },
  { phase: 'recovery', at: 0.0, image: 'kaeshi_sweep', hold: true, tag: 'followThrough' },
  { phase: 'recovery', at: 0.7, image: 'idle', hold: true },
]

let on = false
let slow = false
export function brideKaeshiOn(): boolean { return on }
/** ?brideKaeshi=slow の時 true (ハートが届くまで約 0.3 秒の版)。 */
export function brideKaeshiSlow(): boolean { return on && slow }
// setBrideMotionArt・setBrideIssen の後に呼ぶ (返しの一閃の並びを上書きするため)。
// blade = 返しの一閃をヴェールの布の刃で振り下ろす比較版 (src/bride_kaeshi_blade.ts・ハートは出さない)。
// 既定 = 間の絵＋振りの跡 (both・10-27 から配布版)。old = 前の配布版、tween / trail = 片方だけ (src/bride_kaeshi_tween.ts)。
export function setBrideKaeshi(raw: string | null): void {
  setBrideKaeshiBlade(raw === 'blade')
  // 間の絵＋振りの跡 (both) が配布版の既定 (10-27)。old・new・slow・blade の時だけ外す。
  setBrideKaeshiTween(raw === 'tween' || raw === 'trail' || raw === 'both' ? raw
    : raw === 'old' || raw === 'new' || raw === 'slow' || raw === 'blade' ? 'none' : 'both')
  on = import.meta.env.DEV && (raw === 'new' || raw === 'slow')
  slow = raw === 'slow'
  if (!on) return
  const speed = slow ? KAESHI_SLOW_HEART_SPEED : KAESHI_HEART_SPEED
  const approach = slow ? KAESHI_SLOW_APPROACH_PX : KAESHI_APPROACH_PX
  const a = ATTACKS.veilSweepLink!
  a.kind = 'projectile'
  a.range = approach
  a.approachDistancePx = approach
  a.lungeDistance = 0
  a.crossingPolicy = 'not-applicable'
  a.crossingPolicyExemption = 'travel-projectile'
  a.castProjectile = {
    speedPxPerSec: speed, widthPx: KAESHI_HEART_W,
    spawnForwardPx: KAESHI_HAND_FORWARD, spawnOffsetY: KAESHI_HAND_UP, descentDeg: KAESHI_HEART_DESCENT_DEG,
  }
  BOSS_ATTACK_TIMELINES['bride']!.veilSweepLink = BRIDE_KAESHI_TIMELINE
}

// ───────── 絵 ─────────
const heartImg = typeof Image !== 'undefined' ? new Image() : null
const petalImgs: (HTMLImageElement | null)[] = []
let loaded = false
function ensureImages(): void {
  if (loaded || !heartImg) return
  loaded = true
  heartImg.src = '/sprites/fx/bride/kaeshi_heart.png'
  for (let i = 0; i < KAESHI_PETAL_KINDS; i++) {
    const im = new Image(); im.src = `/sprites/fx/bride/kaeshi_petal_${i}.png`; petalImgs.push(im)
  }
}
const ready = (im: HTMLImageElement | null | undefined): im is HTMLImageElement => !!im && im.complete && im.naturalWidth > 0
export const KAESHI_PETAL_KINDS = 8

// 淡い桜色の3段と、ふちの暗い桜色 (コードで描く時)。
const PINK_HI = '#fbe7ea', PINK_MID = '#f1c9d1', PINK_LO = '#dfa7b4', PINK_LINE = '#9c5f72'

// ───────── 花びら ─────────
/** 花びらを置く間隔 (ハートが進んだ px)。 */
const PETAL_STEP_PX = 18
/** 花びらが舞って消えるまで (秒)。 */
const PETAL_LIFE_S = 0.75
type Petal = { x: number; y: number; t: number; vx: number; vy: number; spin: number; rot0: number; kind: number; size: number }
const petals: Petal[] = []
const trail = new Map<number, { x: number; lastX: number; y: number; dir: number; seen: number }>()
let lastTime = -1

function hash01(n: number): number { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s) }

function spawnPetal(x: number, y: number, t: number, seed: number, dir: number, burst: boolean): void {
  const r = (k: number) => hash01(seed * 3.7 + k)
  petals.push({
    x: x + (r(1) - 0.5) * 10, y: y + (r(2) - 0.5) * 22, t,
    // 軌道の花びらは後ろへ少し流れて、ふわっと浮いてから落ちる。弾いた時は周りへ散る。
    vx: burst ? (r(3) - 0.5) * 260 : -dir * (20 + r(3) * 40),
    vy: burst ? -60 - r(4) * 140 : -25 - r(4) * 30,
    spin: (r(5) - 0.5) * 9, rot0: r(6) * Math.PI * 2, kind: Math.floor(r(7) * KAESHI_PETAL_KINDS) % KAESHI_PETAL_KINDS,
    size: 9 + r(8) * 5,
  })
}

/** 毎フレーム描く。ハート (当たりが生きている間) と、その軌道に残った花びら。 */
export function drawBrideKaeshi(ctx: CanvasRenderingContext2D, hits: readonly ActiveHit[], fx: readonly FxEvent[], time: number): void {
  if (!on) return
  ensureImages()
  if (time < lastTime) { petals.length = 0; trail.clear() }
  lastTime = time
  const alive = new Set<number>()
  for (const hit of hits) {
    if (hit.attackId !== 'veilSweepLink' || hit.follow !== 'travel' || hit.consumed
      || hit.x === undefined || hit.y === undefined || hit.vx === undefined) continue
    alive.add(hit.id)
    const dir = hit.vx >= 0 ? 1 : -1
    const tr = trail.get(hit.id) ?? { x: hit.x, lastX: hit.x, y: hit.y, dir, seen: time }
    // 進んだ分だけ花びらを置く (跳ね返った時は向きが変わるので距離の絶対値で数える)。
    let n = 0
    while (Math.abs(hit.x - tr.lastX) >= PETAL_STEP_PX && n < 6) {
      tr.lastX += Math.sign(hit.x - tr.lastX) * PETAL_STEP_PX
      const k = (tr.lastX - tr.x) / (hit.x - tr.x || 1)
      spawnPetal(tr.lastX, tr.y + (hit.y - tr.y) * k, time, hit.id * 31 + tr.lastX, dir, false)
      n++
    }
    tr.x = hit.x; tr.y = hit.y; tr.dir = dir; tr.seen = time
    trail.set(hit.id, tr)
    drawHeart(ctx, hit.x, hit.y, hit.widthPx ?? KAESHI_HEART_W, dir, time, hit.reflected === true)
  }
  // 消えたハート：弾かれた所 (近くに弾きの火花) なら花びらを散らす。
  for (const [id, tr] of trail) {
    if (alive.has(id)) continue
    trail.delete(id)
    const spark = fx.find(f => f.kind === 'parry_spark' && time - f.t >= 0 && time - f.t < 0.12)
    if (spark?.kind === 'parry_spark' && Math.hypot(spark.x - tr.x, spark.y - tr.y) < 120) {
      for (let i = 0; i < 12; i++) spawnPetal(tr.x, tr.y, time, id * 17 + i * 5.3, tr.dir, true)
    }
  }
  drawPetals(ctx, time)
}

function drawHeart(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, dir: number, time: number, reflected: boolean): void {
  // ふわっと上下に揺れて、少し前へ傾く。跳ね返った時は傾きも逆。
  const bob = Math.sin(time * 9) * 2.5
  const tilt = dir * (0.18 + Math.sin(time * 6) * 0.05)
  ctx.save()
  ctx.translate(x, y + bob)
  ctx.rotate(tilt)
  if (reflected) ctx.globalAlpha = 0.9
  if (ready(heartImg)) {
    const h = w * heartImg.naturalHeight / heartImg.naturalWidth
    ctx.drawImage(heartImg, -w / 2, -h / 2, w, h)
  } else {
    heartPath(ctx, w); ctx.fillStyle = PINK_MID; ctx.fill()
    ctx.save(); heartPath(ctx, w); ctx.clip()
    ctx.fillStyle = PINK_LO; ctx.fillRect(-w / 2, w * 0.05, w, w)
    ctx.fillStyle = PINK_HI; ctx.beginPath(); ctx.ellipse(-w * 0.18, -w * 0.18, w * 0.13, w * 0.09, -0.5, 0, Math.PI * 2); ctx.fill()
    ctx.restore()
    heartPath(ctx, w); ctx.strokeStyle = PINK_LINE; ctx.lineWidth = 2; ctx.stroke()
  }
  ctx.restore()
}

function heartPath(ctx: CanvasRenderingContext2D, w: number): void {
  const s = w / 2
  ctx.beginPath()
  ctx.moveTo(0, s * 0.85)
  ctx.bezierCurveTo(-s * 1.15, s * 0.05, -s * 0.95, -s * 0.95, 0, -s * 0.42)
  ctx.bezierCurveTo(s * 0.95, -s * 0.95, s * 1.15, s * 0.05, 0, s * 0.85)
  ctx.closePath()
}

function drawPetals(ctx: CanvasRenderingContext2D, time: number): void {
  let w = 0
  for (const p of petals) {
    const age = time - p.t
    if (age < 0 || age > PETAL_LIFE_S) continue
    petals[w++] = p
    const u = age / PETAL_LIFE_S
    // 浮いてから落ちる (重力)。左右にゆらゆら。最後の 4 割で薄れる。
    const px = p.x + p.vx * age + Math.sin(age * 7 + p.rot0) * 5
    const py = p.y + p.vy * age + 160 * age * age
    const alpha = u < 0.6 ? 1 : 1 - (u - 0.6) / 0.4
    ctx.save()
    ctx.globalAlpha = alpha
    ctx.translate(px, py)
    ctx.rotate(p.rot0 + p.spin * age)
    // 舞う向きで細く見える (横向きにひっくり返る)。
    ctx.scale(1, 0.45 + 0.55 * Math.abs(Math.cos(age * 5 + p.rot0)))
    const im = petalImgs[p.kind]
    if (ready(im)) {
      const h = p.size * im.naturalHeight / im.naturalWidth
      ctx.drawImage(im, -p.size / 2, -h / 2, p.size, h)
    } else {
      ctx.beginPath(); ctx.ellipse(0, 0, p.size / 2, p.size / 3.2, 0, 0, Math.PI * 2)
      ctx.fillStyle = p.kind % 2 ? PINK_HI : PINK_MID; ctx.fill()
      ctx.strokeStyle = PINK_LINE; ctx.lineWidth = 1; ctx.stroke()
    }
    ctx.restore()
  }
  petals.length = w
}
